import { useEffect, useMemo, useRef, useState } from "react";
import { useProjectActions } from "@contexts/projects";
import UpdateJobStages from "@components/dashboard/UpdateJobStages";

// Rótulos dos campos que o back-end pode devolver em `missing_fields` — a lista vem da
// projeção que o data-pipeline faz sobre `buildings`, então usa os nomes do banco.
const MISSING_FIELD_LABELS = {
  active: 'Obra ativa (o pipeline só processa obras com active = true)',
  company_id: 'Construtora',
  company_code: 'Código da Construtora',
  building_code: 'Código da Obra',
  building_name: 'Nome da Obra',
  budget_version: 'Versão do Orçamento',
  cost_centers_id: 'Centros de Custo',
  building_cost_centers_id: 'Centros de Custo da Obra',
  appropriation_level: 'Nível de Apropriação',
  plan_tool: 'Ferramenta de Planejamento',
  scope: 'Escopo',
  start_date: 'Data de Início',
  budget_delivery_date: 'Data de Entrega do Orçamento',
  budget_sheet: 'Código do Orçamento (MEGA)',
  budget_sheet_base64:
    'Identificador do orçamento no MEGA — gerado ao salvar o código do orçamento. Reabra EDITAR e salve.',
  indexer: 'Índice de correção',
  'indexer.description': 'Índice de correção (Índice)',
  'budget_delivery_date.not_first_of_month':
    'Mês de Entrega do Orçamento — a data precisa cair no dia 1º. A série do CUB/INCC é mensal, '
    + 'e o pipeline busca o índice por data exata. Reabra EDITAR e salve para normalizar.',
}

const GROUP_LABELS = {
  internal: 'Usuários internos (Ampli / Cartesian)',
  company: 'Usuários da construtora',
}

const userLabel = (user) => user.name || user.email || user._id
const isSelectableUser = (user) => user.active !== false && !user.locked

/**
 * Fluxo de "Registrar dados" — a primeira execução do pipeline para uma obra recém-cadastrada.
 *
 * A liberação de acesso vem ANTES de executar: é a mesma decisão operacional ("colocar a obra
 * no ar para essas pessoas"), e não faz sentido rodar o pipeline para depois descobrir quem
 * pode ver. Como `users.buildingIds` guarda o `_id` de `wbs_buildings` — que só existe depois
 * da ingestão — a seleção é registrada como PENDENTE em `buildings.pending_access_user_ids` e
 * aplicada pelo worker de notificações quando o job conclui.
 *
 * Dois passos: (1) escolher os usuários, (2) confirmar. Sem cadastro completo, o passo 1 já
 * mostra o que impede a execução e o botão fica travado.
 */
const BuildingFirstRunModal = ({ buildingId, onDone }) => {
  const [status, setStatus] = useState(null)
  const [selected, setSelected] = useState([])
  const [loading, setLoading] = useState(true)
  const [confirming, setConfirming] = useState(false)
  const [feedback, setFeedback] = useState(null)
  // `tracking`: job em andamento cujo progresso está sendo acompanhado na tela.
  const [tracking, setTracking] = useState(null)
  const selectionBuildingRef = useRef(null)
  const { getBuildingBootstrap, bootstrapBuilding, getBootstrapJob } = useProjectActions();

  const load = async () => {
    setLoading(true)
    const res = await getBuildingBootstrap(buildingId)
    if (res?.ok) {
      setStatus(res)
      // Default é TODOS os elegíveis marcados — internos e da construtora vinculada. A intenção
      // do fluxo é "habilitar a obra", e a tela de confirmação existe para DESMARCAR quem não
      // deve ver, não para marcar um a um. Com a lista vazia por default, o operador precisava
      // lembrar de marcar e a obra saía liberada só para quem ele clicou.
      // Numa retentativa, respeita a seleção anterior em vez de reexpandir para todos.
      const users = res.users || []
      const validUsers = users.filter(isSelectableUser)
      const hasExplicitPendingSelection = Object.prototype.hasOwnProperty.call(
        res,
        'pending_access_user_ids',
      ) && Array.isArray(res.pending_access_user_ids)
      const legacyRetrySelection = !hasExplicitPendingSelection
        && Array.isArray(res.pending_user_ids)
        && (res.job || res.pending_user_ids.length > 0)
        ? res.pending_user_ids
        : null
      const pendingSelection = hasExplicitPendingSelection
        ? res.pending_access_user_ids
        : legacyRetrySelection
      const firstLoadForBuilding = selectionBuildingRef.current !== buildingId
      selectionBuildingRef.current = buildingId

      setSelected(current => {
        if (!firstLoadForBuilding) {
          const validIds = new Set(validUsers.map(user => user._id))
          return current.filter(userId => validIds.has(userId))
        }
        if (Array.isArray(pendingSelection)) {
          const pendingIds = new Set(pendingSelection)
          return validUsers.filter(user => pendingIds.has(user._id)).map(user => user._id)
        }
        const previous = validUsers.filter(user => user.has_access || user.pending)
        return (previous.length > 0 ? previous : validUsers).map(user => user._id)
      })
      // Reabrir o modal durante uma execução cai direto no acompanhamento, em vez de oferecer
      // um botão que responderia 409.
      if (['queued', 'running'].includes(res.job?.status)) setTracking(res.job)
    } else {
      setFeedback({ ok: false, message: res?.message || 'Não foi possível verificar o status da obra.' })
    }
    setLoading(false)
  }

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [buildingId])

  const users = status?.users || []
  const missing = status?.missing_fields || []
  const activeJob = ['queued', 'running'].includes(status?.job?.status)

  const toggle = (userId) => {
    setSelected(prev => (prev.includes(userId) ? prev.filter(id => id !== userId) : [...prev, userId]))
  }

  const selectAll = (group) => {
    const groupIds = users.filter(u => u.group === group && isSelectableUser(u)).map(u => u._id)
    setSelected(prev => [...new Set([...prev, ...groupIds])])
  }

  const clearGroup = (group) => {
    const groupIds = new Set(users.filter(u => u.group === group).map(u => u._id))
    setSelected(prev => prev.filter(id => !groupIds.has(id)))
  }

  const chosen = useMemo(
    () => users.filter(u => selected.includes(u._id) && isSelectableUser(u)),
    [users, selected],
  )

  const blocked = missing.length > 0 || activeJob || status?.supported === false

  const run = async () => {
    const res = await bootstrapBuilding(buildingId, selected)
    if (res?.ok) {
      // Não fecha: passa a acompanhar as etapas. Quem fecha é o onFinished, depois do
      // terminal — é ali que o "auto refresh" da listagem acontece.
      setConfirming(false)
      setTracking(res.job ?? { status: 'queued' })
      return
    }
    setFeedback({
      ok: false,
      message: res?.message
        || (res?.missing_fields?.length ? `Campos ausentes: ${res.missing_fields.join(', ')}` : 'Não foi possível iniciar.'),
    })
    setConfirming(false)
  }

  return (
    <div className="backoffice-form-card">
      <h2>Registrar dados — {status?.building_name || 'obra'}</h2>

      {loading && <p role="status" aria-live="polite">Verificando status da obra...</p>}

      {tracking && (
        <>
          <p>
            Execução em andamento. Você pode fechar esta janela — o processamento continua e o
            acesso é liberado ao final.
          </p>
          <UpdateJobStages
            initialJob={tracking}
            fetchJob={() => getBootstrapJob(buildingId)}
            onFinished={(job) => onDone?.({
              ok: job.status === 'succeeded',
              message: job.status === 'succeeded'
                ? 'Ingestão concluída. A obra já aparece para os usuários liberados.'
                : `A execução falhou${job.error_code ? ` (${job.error_code})` : ''}.`,
            })}
          />
        </>
      )}

      {!loading && !tracking && status && !confirming && (
        <>
          <p>
            Esta é a primeira execução do pipeline. Até ela terminar, a obra não aparece para
            nenhum usuário. Todos os elegíveis já vêm marcados — <strong>desmarque quem não deve
            ter acesso</strong>. A liberação é aplicada automaticamente quando a ingestão concluir.
          </p>

          {missing.length > 0 && (
            <div className="bootstrap-missing">
              <p>Cadastro incompleto — o pipeline não roda sem estes campos. Use EDITAR para corrigi-los:</p>
              <ul>
                {missing.map(field => (
                  <li key={field}>
                    {field === 'indexer.series_missing'
                      ? `Mês de Entrega do Orçamento — não há publicação do índice para o mês escolhido.${
                          status.indexer_latest_month
                            ? ` O último mês disponível é ${status.indexer_latest_month}.`
                            : ''
                        }`
                      : (MISSING_FIELD_LABELS[field] || field)}
                  </li>
                ))}
              </ul>
            </div>
          )}

          {status.supported === false && (
            <p>A integração desta construtora ({status.erp || 'ERP desconhecido'}) não suporta a execução completa do pipeline.</p>
          )}

          {activeJob && <p>Já existe uma execução em andamento para esta obra.</p>}

          {['internal', 'company'].map(group => {
            const groupUsers = users.filter(u => u.group === group)
            if (groupUsers.length === 0) return null
            return (
              <div className="access-group" key={group}>
                <div className="access-group-header">
                  <strong>{GROUP_LABELS[group]}</strong>
                  <button type="button" className="button" onClick={() => selectAll(group)}>Marcar todos</button>
                  <button type="button" className="button" onClick={() => clearGroup(group)}>Desmarcar todos</button>
                </div>
                {groupUsers.map(user => (
                  <label key={user._id} className="access-user-row">
                    <input
                      type="checkbox"
                      checked={selected.includes(user._id)}
                      disabled={!isSelectableUser(user)}
                      onChange={() => toggle(user._id)}
                    />
                    {userLabel(user)}
                    {user.email && user.name && <span className="access-user-email"> — {user.email}</span>}
                    {user.active === false && <span className="inactive-tag">Inativo</span>}
                    {user.locked && <span className="inactive-tag">Conta de apresentação</span>}
                  </label>
                ))}
              </div>
            )
          })}

          <div className="edit-user-actions">
            <button
              type="button"
              className="button button-accent"
              disabled={blocked}
              onClick={() => setConfirming(true)}
            >
              Revisar e executar
            </button>
            <button type="button" className="button" onClick={load}>Atualizar status</button>
          </div>
        </>
      )}

      {!loading && !tracking && status && confirming && (
        <div className="access-confirm">
          <p>
            Confirma disparar a primeira execução de "{status.building_name}" e liberar o acesso
            para {chosen.length} usuário(s)?
          </p>

          {chosen.length === 0 ? (
            <p>Nenhum usuário selecionado — a obra será processada, mas só administradores a verão.</p>
          ) : (
            <ul>
              {chosen.map(u => <li key={u._id}>{userLabel(u)} ({GROUP_LABELS[u.group]})</li>)}
            </ul>
          )}

          <p>
            A execução consome cota de API da construtora e pode levar alguns minutos. Você
            recebe um e-mail quando terminar.
          </p>

          <div className="edit-user-actions">
            <button type="button" className="button button-accent" onClick={run}>Confirmar e executar</button>
            <button type="button" className="button" onClick={() => setConfirming(false)}>Voltar</button>
          </div>
        </div>
      )}

      {feedback && (
        <p className={feedback.ok ? 'directory-feedback directory-feedback--ok' : 'directory-feedback directory-feedback--error'}>
          {feedback.message}
        </p>
      )}
    </div>
  );
};

export default BuildingFirstRunModal;
